import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import colors from '../../config/colors';

const SingleChoiceQuestion = ({ question, value, onChange }) => {
  const isCompactMode = question.displayMode === 'compact';
  const isCapsuleMode = question.displayMode === 'capsule';
  const isGroupedChip = question.displayMode === 'grouped-chip' && Array.isArray(question.groups);
  // Auto-compact when a question has 4+ options and no explicit displayMode —
  // keeps the form scroll-light without touching every question definition.
  const shouldAutoCompact = !question.displayMode
    && Array.isArray(question.options)
    && question.options.length >= 4;

  // Grouped-chip layout — used by the location question (Q21)
  if (isGroupedChip) {
    const Chip = ({ option, testid }) => {
      const isSelected = value === option.value;
      return (
        <motion.button
          type="button"
          key={option.value}
          data-testid={testid}
          onClick={() => onChange(option.value)}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          style={{
            background: isSelected ? colors.brandPrimary : colors.bgCard,
            color: isSelected ? colors.textInverse : colors.textPrimary,
            border: `2px solid ${isSelected ? colors.brandPrimary : colors.borderMedium}`,
            borderRadius: '9999px',
            padding: '10px 18px',
            fontSize: '0.95rem',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: isSelected ? `0 4px 14px ${colors.brandPrimary}44` : 'none',
          }}
        >
          {option.label}
        </motion.button>
      );
    };

    return (
      <div style={{ padding: '12px 0' }}>
        <h2 style={{
          fontSize: 'clamp(1.5rem, 3vw, 2rem)',
          fontWeight: 700,
          color: colors.textPrimary,
          marginBottom: '8px',
          lineHeight: 1.2,
        }}>
          {question.question}
        </h2>

        {question.subtitle && (
          <p style={{ fontSize: '1rem', color: colors.textSecondary, marginBottom: '20px' }}>
            {question.subtitle}
          </p>
        )}

        <div
          data-testid="q21-grouped-chips"
          style={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '900px' }}
        >
          {question.groups.map((group) => (
            <div key={group.label}>
              <div style={{
                fontSize: '0.78rem',
                fontWeight: 800,
                letterSpacing: '0.16em',
                color: colors.textMuted,
                marginBottom: '4px',
              }}>
                {group.label}
              </div>
              <div style={{
                fontSize: '0.85rem',
                color: colors.textSecondary,
                marginBottom: '12px',
                lineHeight: 1.4,
              }}>
                {group.descriptor}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {group.options.map((opt) => (
                  <Chip key={opt.value} option={opt} testid={`q21-chip-${opt.value}`} />
                ))}
              </div>
            </div>
          ))}

          {question.flexibleOption && (
            <>
              <div style={{ height: 1, background: colors.borderMedium, margin: '4px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Chip
                  option={question.flexibleOption}
                  testid={`q21-chip-${question.flexibleOption.value}`}
                />
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Capsule layout for country selector (horizontal wrap)
  if (isCapsuleMode) {
    return (
      <div style={{ padding: '12px 0' }}>
        <h2 style={{
          fontSize: 'clamp(1.5rem, 3vw, 2rem)',
          fontWeight: '700',
          color: colors.textPrimary,
          marginBottom: '8px',
          lineHeight: '1.2'
        }}>
          {question.question}
        </h2>

        {question.subtitle && (
          <p style={{
            fontSize: '1rem',
            color: colors.textSecondary,
            marginBottom: '20px'
          }}>
            {question.subtitle}
          </p>
        )}

        <div style={{ 
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          maxWidth: '900px'
        }}>
          {question.options.map((option) => {
            const isSelected = value === option.value;
            
            return (
              <motion.button
                key={option.value}
                onClick={() => onChange(option.value)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  background: isSelected ? '#E8612A' : '#F3F4F6',
                  border: 'none',
                  borderRadius: '9999px',
                  padding: '12px 24px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontSize: '0.95rem',
                  fontWeight: '500',
                  color: isSelected ? '#FFFFFF' : '#1F2937',
                  boxShadow: isSelected ? '0 2px 8px rgba(232, 97, 42, 0.3)' : 'none'
                }}
              >
                {option.label}
              </motion.button>
            );
          })}
        </div>
      </div>
    );
  }

  if (isCompactMode || shouldAutoCompact) {
    // Compact capsule layout for minimal scrolling
    return (
      <div style={{ padding: '12px 0' }}>
        <h2 style={{
          fontSize: 'clamp(1.5rem, 3vw, 2rem)',
          fontWeight: '700',
          color: colors.textPrimary,
          marginBottom: '8px',
          lineHeight: '1.2'
        }}>
          {question.question}
        </h2>

        {question.subtitle && (
          <p style={{
            fontSize: '1rem',
            color: colors.textSecondary,
            marginBottom: '20px'
          }}>
            {question.subtitle}
          </p>
        )}

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '12px',
          maxWidth: '900px'
        }}>
          {question.options.map((option) => {
            const isSelected = value === option.value;
            
            return (
              <motion.button
                key={option.value}
                onClick={() => onChange(option.value)}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                style={{
                  background: isSelected ? colors.brandPrimary : colors.bgCard,
                  border: `2px solid ${isSelected ? colors.brandPrimary : colors.borderMedium}`,
                  borderRadius: '10rem',
                  padding: '16px 20px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  minHeight: '70px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px'
                }}
              >
                <div style={{
                  fontSize: '1.05rem',
                  fontWeight: '700',
                  color: isSelected ? colors.textInverse : colors.textPrimary
                }}>
                  {option.label}
                </div>
                {option.subtext && (
                  <div style={{
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    color: isSelected ? 'rgba(255,255,255,0.85)' : colors.textMuted
                  }}>
                    {option.subtext}
                  </div>
                )}
                
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '12px'
                    }}
                  >
                    <Check size={16} color={colors.textInverse} strokeWidth={3} />
                  </motion.div>
                )}
              </motion.button>
            );
          })}
        </div>
      </div>
    );
  }

  // Default vertical layout
  return (
    <div style={{ padding: '12px 0' }}>
      <h2 style={{
        fontSize: 'clamp(1.5rem, 3vw, 2rem)',
        fontWeight: '700',
        color: colors.textPrimary,
        marginBottom: '8px',
        lineHeight: '1.2'
      }}>
        {question.question}
      </h2>

      {question.subtitle && (
        <p style={{
          fontSize: '1rem',
          color: colors.textSecondary,
          marginBottom: '20px'
        }}>
          {question.subtitle}
        </p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {question.options.map((option) => {
          const isSelected = value === option.value;
          
          return (
            <motion.button
              key={option.value}
              onClick={() => onChange(option.value)}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              style={{
                background: isSelected ? colors.brandPrimary : colors.bgCard,
                border: `2px solid ${isSelected ? colors.brandPrimary : colors.borderMedium}`,
                borderRadius: '12px',
                padding: '20px 24px',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                position: 'relative'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                {/* Checkbox */}
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  border: `2px solid ${isSelected ? colors.textInverse : colors.borderStrong}`,
                  background: isSelected ? colors.textInverse : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginTop: '2px'
                }}>
                  {isSelected && <Check size={16} color={colors.brandPrimary} strokeWidth={3} />}
                </div>

                {/* Content */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    color: isSelected ? colors.textInverse : colors.textPrimary,
                    marginBottom: option.subtext ? '4px' : 0
                  }}>
                    {option.label}
                  </div>
                  {option.subtext && (
                    <div style={{
                      fontSize: '0.9rem',
                      color: isSelected ? 'rgba(255,255,255,0.8)' : colors.textMuted
                    }}>
                      {option.subtext}
                    </div>
                  )}
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

export default SingleChoiceQuestion;
